import * as React from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, Save, Info, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { EmptyState } from "@/components/feel/EmptyState";
import { Skeleton } from "@/components/feel/Skeleton";

/**
 * Rules — Rule Recipes surface (Phase 9).
 *
 * Lists the cross-module rules ("recipes") and lets an operator with
 * rules.manage enable/disable each recipe and (where parameterizable)
 * tune its thresholds.
 *
 * Phase 11 — also lists the board recipes (cards from events).  These
 * share the ``company_rule_settings.enabled`` map but default to OFF
 * (recipes create work — opt-in).
 */
export default function Rules() {
  const { companyId } = useParams();
  const { has } = useCompany();
  const canManage = has("rules.manage") || has("company.manage");

  const [rules, setRules] = React.useState(null);
  const [recipes, setRecipes] = React.useState(null);
  const [settings, setSettings] = React.useState(null);
  const [dirty, setDirty] = React.useState({ enabled: {}, thresholds: {} });
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const [{ data: r }, { data: s }, { data: br }] = await Promise.all([
        api.get(`/companies/${companyId}/alerts/rules`),
        api.get(`/companies/${companyId}/rule-settings`),
        api.get(`/companies/${companyId}/board-recipes`),
      ]);
      setRules(r.rules || []);
      setRecipes(br.recipes || []);
      setSettings(s || { enabled: {}, thresholds: {} });
      setDirty({ enabled: {}, thresholds: {} });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, [companyId]);

  React.useEffect(() => { load(); }, [load]);

  function isEnabled(ruleId, defaultOn = true) {
    if (Object.prototype.hasOwnProperty.call(dirty.enabled, ruleId)) return dirty.enabled[ruleId];
    const v = settings?.enabled?.[ruleId];
    return v === undefined ? defaultOn : !!v;
  }
  function threshold(rule, param) {
    if (dirty.thresholds[rule.id]?.[param] !== undefined) return dirty.thresholds[rule.id][param];
    if (settings?.thresholds?.[rule.id]?.[param] !== undefined) return settings.thresholds[rule.id][param];
    return rule.parameterizable?.[param]?.default;
  }

  function toggle(ruleId, v) {
    setDirty((x) => ({ ...x, enabled: { ...x.enabled, [ruleId]: v } }));
  }
  function setThreshold(ruleId, param, v) {
    setDirty((x) => ({
      ...x,
      thresholds: {
        ...x.thresholds,
        [ruleId]: { ...(x.thresholds[ruleId] || {}), [param]: v },
      },
    }));
  }
  const isDirty = Object.keys(dirty.enabled).length > 0 || Object.keys(dirty.thresholds).length > 0;

  async function save() {
    if (busy || !isDirty) return;
    setBusy(true);
    try {
      const payload = {};
      if (Object.keys(dirty.enabled).length) payload.enabled = dirty.enabled;
      if (Object.keys(dirty.thresholds).length) {
        payload.thresholds = {};
        for (const [rid, params] of Object.entries(dirty.thresholds)) {
          payload.thresholds[rid] = {};
          for (const [k, v] of Object.entries(params)) {
            payload.thresholds[rid][k] = Number(v);
          }
        }
      }
      await api.patch(`/companies/${companyId}/rule-settings`, payload);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  }

  if (rules === null) {
    return (
      <div className="px-4 md:px-6 py-4 md:py-6" data-testid="rules-loading">
        <Skeleton className="h-4 w-32" />
        <div className="mt-4 space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    );
  }

  if (rules.length === 0) {
    return (
      <div className="p-6" data-testid="rules-empty">
        <EmptyState icon={ShieldCheck} eyebrow="Rules"
          title="No rules registered." body="Rules ship as code; check back after the next release." />
      </div>
    );
  }

  const bySeverity = { alert: [], warn: [], info: [] };
  for (const r of rules) (bySeverity[r.severity] || bySeverity.info).push(r);

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1100px]" data-testid="rules-page">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="micro-label">Configuration</div>
          <h1 className="mt-1 text-[22px] font-bold tracking-tight text-brand-ink flex items-center gap-2">
            <ShieldCheck size={18} className="text-brand-violet"/> Rule recipes
          </h1>
          <div className="mt-1 text-[12px] text-brand-muted max-w-[720px]">
            Every rule that can fire an alert. Turn any recipe off if it doesn't fit your operation;
            the sweep will honour your choice on the next tick.
          </div>
        </div>
        {canManage && (
          <button className="btn-violet" onClick={save} disabled={busy || !isDirty}
                  data-testid="rules-save">
            <Save size={13}/> {busy ? "…" : (isDirty ? "Save changes" : "No changes")}
          </button>
        )}
      </div>

      {!canManage && (
        <div className="mb-4 rounded-md border border-brand-rule bg-brand-canvas px-3 py-2 flex items-center gap-2 text-[12px] text-brand-muted"
             data-testid="rules-readonly">
          <Info size={13}/> Read-only — you need <span className="mono-num">rules.manage</span> to change these.
        </div>
      )}

      {["alert", "warn", "info"].map((sev) => {
        const items = bySeverity[sev] || [];
        if (!items.length) return null;
        return (
          <section key={sev} className="mt-6" data-testid={`rules-section-${sev}`}>
            <div className="micro-label mb-1">{sev.toUpperCase()}</div>
            <div className="card-panel overflow-hidden">
              {items.map((r, i) => (
                <div key={r.id}
                     className={`px-4 py-3 flex items-start gap-3 ${i > 0 ? 'border-t border-brand-rule' : ''}`}
                     data-testid={`rules-row-${r.id}`}>
                  <div className="mt-0.5">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="accent-brand-violet h-4 w-4"
                             checked={isEnabled(r.id)}
                             disabled={!canManage}
                             onChange={(e) => toggle(r.id, e.target.checked)}
                             data-testid={`rules-toggle-${r.id}`} />
                    </label>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-[13px] font-semibold text-brand-ink">{r.name}</div>
                      <span className={`chip mono-num text-[10px] ${
                        sev === 'alert' ? 'chip-coral' : sev === 'warn' ? 'chip-amber' : ''}`}>
                        {sev}
                      </span>
                      <span className="chip mono-num text-[10px]">{r.target_module}</span>
                      <span className="text-[10.5px] mono-num text-brand-faint">{r.id}</span>
                    </div>
                    <div className="mt-1 text-[12px] text-brand-muted leading-relaxed">{r.description}</div>
                    {r.parameterizable && (
                      <div className="mt-2 flex flex-wrap gap-3">
                        {Object.entries(r.parameterizable).map(([pk, spec]) => (
                          <div key={pk} className="flex items-center gap-1.5">
                            <span className="text-[11px] text-brand-muted">{spec.label || pk}</span>
                            <input type="number" min={spec.min ?? 0} max={spec.max ?? 365}
                                   className="field-input !h-7 !w-20 mono-num"
                                   value={threshold(r, pk) ?? ""}
                                   disabled={!canManage || !isEnabled(r.id)}
                                   onChange={(e) => setThreshold(r.id, pk, e.target.value)}
                                   data-testid={`rules-threshold-${r.id}-${pk}`} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {/* Phase 11 — Board recipes (cards from events) */}
      {recipes && recipes.length > 0 && (
        <section className="mt-8" data-testid="rules-section-recipes">
          <div className="micro-label mb-1 flex items-center gap-1.5">
            <Zap size={11} className="text-brand-violet" /> BOARD RECIPES (CARDS FROM EVENTS)
          </div>
          <div className="text-[11.5px] text-brand-muted mb-2 max-w-[720px]">
            Turn cross-module signals into work. Enabled recipes create cards on the "Operations" board (auto-created on first fire). Every card is dedup-guarded per source event.
          </div>
          <div className="card-panel overflow-hidden">
            {recipes.map((r, i) => (
              <div key={r.id}
                   className={`px-4 py-3 flex items-start gap-3 ${i > 0 ? 'border-t border-brand-rule' : ''}`}
                   data-testid={`rules-recipe-row-${r.id}`}>
                <div className="mt-0.5">
                  <input type="checkbox" className="accent-brand-violet h-4 w-4"
                         checked={isEnabled(r.id, false)}
                         disabled={!canManage}
                         onChange={(e) => toggle(r.id, e.target.checked)}
                         data-testid={`rules-recipe-toggle-${r.id}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-[13px] font-semibold text-brand-ink">{r.name}</div>
                    <span className={`chip mono-num text-[10px] ${
                      r.severity === 'alert' ? 'chip-coral' : r.severity === 'warn' ? 'chip-amber' : ''}`}>
                      {r.severity}
                    </span>
                    <span className="chip mono-num text-[10px]">boards</span>
                    <span className="text-[10.5px] mono-num text-brand-faint">{r.id}</span>
                  </div>
                  <div className="mt-1 text-[12px] text-brand-muted leading-relaxed">{r.description}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
